import React from 'react';
import PropTypes from 'prop-types';
import { useFieldDecimalPlacesConfig } from 'hooks';
import { useTranslation } from 'react-i18next';
import { GenericFilterParamTableCell } from '../DetailsStyles';
import { TableBody, TableRow, TableHead, TableCell, Grid, Tooltip, Icon } from '@mui/material';
import { Button, TextField, Typography } from '@totalsoft/rocket-ui';
import { GenericFilterParamTable } from '../DetailsStyles';
import { StyledPhraseTextField, StyledLabelContainer, StyledPhrasePaper } from '../DetailsStyles';
import InfoIcon from '@mui/icons-material/Info';
import { get, set } from '@totalsoft/change-tracking-react';
import { emptyString } from 'utils/constants';
import { usePermission } from 'providers/RightsHooks';
import { ADMIN_INSURANCE_CONVENTIONS_WRITE } from 'constants/rightsCodes';
import FlexBox from 'components/common/FlexBox';

const InsuranceConventionPercentCalculationComponent = ({ params, lens, onSave, saving, isDirty, isNewConvention }) => {
  const { t } = useTranslation();
  const { cvsChAmount } = useFieldDecimalPlacesConfig();
  const canWrite = usePermission(ADMIN_INSURANCE_CONVENTIONS_WRITE);

  return (
    <Grid container flexDirection='column' gap={1} p={2}>
      {isNewConvention ? (
        <FlexBox gap={1} alignItems='center' justifyContent='center'>
          <Icon fontSize='small' color='primary'>
            error
          </Icon>
          <Typography color='textPrimary'>{t('InsuranceConventions.Details.Warning')}</Typography>
        </FlexBox>
      ) : (
        <>
          <Grid item container>
            <Grid item xs={12} md={6} p={1}>
              <StyledPhrasePaper elevation={2}>
                <StyledLabelContainer>
                  <span>{t('InsuranceConventions.Details.PercentCalculation.PercentFormula')}</span>
                  <Tooltip title={t('InsuranceConventions.Details.PercentCalculation.PhraseExplanation')}>
                    <InfoIcon />
                  </Tooltip>
                </StyledLabelContainer>
                <StyledPhraseTextField
                  placeholder={t('InsuranceConventions.Details.PercentCalculation.PercentFormula')}
                  value={get(lens.baseInsurancePercentFormula) || emptyString}
                  onChange={event => set(lens.baseInsurancePercentFormula)(event)}
                  disabled={saving || !canWrite}
                />
              </StyledPhrasePaper>
            </Grid>
            <Grid item xs={12} md={6} p={1}>
              <StyledPhrasePaper elevation={2}>
                <StyledLabelContainer>
                  <span>{t('InsuranceConventions.Details.PercentCalculation.AmountFormula')}</span>
                  <Tooltip title={t('InsuranceConventions.Details.PercentCalculation.PhraseExplanation')}>
                    <InfoIcon />
                  </Tooltip>
                </StyledLabelContainer>
                <StyledPhraseTextField
                  placeholder={t('InsuranceConventions.Details.PercentCalculation.AmountFormula')}
                  value={get(lens.insuranceValueFormula) || emptyString}
                  onChange={event => set(lens.insuranceValueFormula)(event)}
                  disabled={saving || !canWrite}
                />
              </StyledPhrasePaper>
            </Grid>
            <Grid item xs={12} p={2}>
              <TextField
                fullWidth
                isNumeric
                minValue={0}
                value={get(lens.minInsuranceAmount) || 0}
                onChange={event => set(lens.minInsuranceAmount)(event)}
                decimalScale={cvsChAmount}
                label={t('InsuranceConventions.Details.PercentCalculation.MinimumAnnualPremium')}
                disabled={saving || !canWrite}
              />
            </Grid>
          </Grid>
          <Grid item xs={12} p={1}>
            <GenericFilterParamTable>
              <TableHead>
                <TableRow>
                  <GenericFilterParamTableCell>
                    {t('InsuranceConventions.Details.PercentCalculation.ParameterCode')}
                  </GenericFilterParamTableCell>
                  <GenericFilterParamTableCell>
                    {t('InsuranceConventions.Details.PercentCalculation.ParameterName')}
                  </GenericFilterParamTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {params.length > 0 &&
                  params.map(param => (
                    <TableRow key={param.paramId}>
                      <>
                        <TableCell>{param.paramCode || '-'}</TableCell>
                        <TableCell>{param.paramName || '-'}</TableCell>
                      </>
                    </TableRow>
                  ))}
              </TableBody>
            </GenericFilterParamTable>
          </Grid>
          <Grid container item xs={12} pt={2} justifyContent='flex-end'>
            <Button color='primary' variant='contained' onClick={onSave} disabled={!isDirty || saving || !canWrite}>
              {saving ? t('Saving') : t('Save')}
            </Button>
          </Grid>
        </>
      )}
    </Grid>
  );
};

InsuranceConventionPercentCalculationComponent.propTypes = {
  params: PropTypes.array,
  lens: PropTypes.object,
  onSave: PropTypes.func,
  saving: PropTypes.bool,
  isDirty: PropTypes.bool,
  isNewConvention: PropTypes.bool
};

export default InsuranceConventionPercentCalculationComponent;
